import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Database, Download, FileJson, RefreshCw, 
  HardDrive, CheckCircle, AlertTriangle, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DatabaseBackup({ user, onLogout }) {
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState(null);
  const [downloadingCollection, setDownloadingCollection] = React.useState(null);
  const [backupHistory, setBackupHistory] = React.useState([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Fetch database summary on load
  React.useEffect(() => {
    fetchSummary();
    // Load backup history from localStorage
    const history = JSON.parse(localStorage.getItem('backup_history') || '[]');
    setBackupHistory(history);
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/backup/summary`, getAuthHeader());
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      toast.error('Failed to fetch database summary');
    } finally {
      setLoading(false);
    }
  };

  // Download full backup
  const downloadFullBackup = async () => {
    try {
      setLoading(true);
      toast.info('Creating full backup... This may take a moment.');
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/backup/full`, getAuthHeader());
      
      // Create and download file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `autobook_full_backup_${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Save to backup history
      const newBackup = {
        id: Date.now(),
        type: 'Full Backup',
        date: new Date().toISOString(),
        collections: response.data.backup_info.total_collections,
        documents: response.data.backup_info.total_documents,
        filename: `autobook_full_backup_${timestamp}.json`
      };
      const updatedHistory = [newBackup, ...backupHistory].slice(0, 10);
      setBackupHistory(updatedHistory);
      localStorage.setItem('backup_history', JSON.stringify(updatedHistory));
      
      toast.success(`Full backup downloaded! ${response.data.backup_info.total_documents} documents backed up.`);
    } catch (error) {
      console.error('Full backup failed:', error);
      toast.error('Failed to create full backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Download single collection backup
  const downloadCollectionBackup = async (collectionName) => {
    try {
      setDownloadingCollection(collectionName);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/backup/collection/${collectionName}`, 
        getAuthHeader()
      );
      
      // Create and download file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `autobook_${collectionName}_${timestamp}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${collectionName} backup downloaded! ${response.data.count} documents.`);
    } catch (error) {
      console.error('Collection backup failed:', error);
      toast.error('Failed to backup collection');
    } finally {
      setDownloadingCollection(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  // Get collection icon color based on importance
  const getCollectionColor = (name) => {
    const important = ['bookings', 'leads', 'users', 'customers'];
    const medium = ['stock', 'masters', 'audit_logs'];
    if (important.some(i => name.includes(i))) return 'text-red-600 bg-red-50';
    if (medium.some(m => name.includes(m))) return 'text-amber-600 bg-amber-50';
    return 'text-blue-600 bg-blue-50';
  };

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Database Backup">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Database className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Database Backup</h1>
          </div>
          <p className="text-indigo-100">
            Create backups of your data before deployment or for safekeeping
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={downloadFullBackup}
            disabled={loading}
            className="flex items-center justify-center gap-3 p-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg disabled:opacity-50"
          >
            {loading ? (
              <RefreshCw className="w-6 h-6 animate-spin" />
            ) : (
              <Download className="w-6 h-6" />
            )}
            <div className="text-left">
              <div className="font-bold text-lg">Download Full Backup</div>
              <div className="text-sm text-green-100">All collections in one JSON file</div>
            </div>
          </button>

          <button
            onClick={fetchSummary}
            disabled={loading}
            className="flex items-center justify-center gap-3 p-6 bg-white border-2 border-indigo-200 text-indigo-700 rounded-xl hover:bg-indigo-50 transition-all"
          >
            <RefreshCw className={`w-6 h-6 ${loading ? 'animate-spin' : ''}`} />
            <div className="text-left">
              <div className="font-bold text-lg">Refresh Summary</div>
              <div className="text-sm text-gray-500">Update collection counts</div>
            </div>
          </button>
        </div>

        {/* Database Summary */}
        {summary && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-lg">{summary.database_name}</h2>
                  <p className="text-sm text-gray-500">
                    {summary.total_collections} collections • {summary.total_documents.toLocaleString()} total documents
                  </p>
                </div>
                <div className="text-right text-xs text-gray-400">
                  Last updated: {formatDate(summary.generated_at)}
                </div>
              </div>
            </div>

            {/* Collections List */}
            <div className="divide-y">
              {summary.collections.map((coll) => (
                <div 
                  key={coll.name}
                  className="flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getCollectionColor(coll.name)}`}>
                      <FileJson className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-medium">{coll.name}</div>
                      <div className="text-sm text-gray-500">
                        {coll.document_count.toLocaleString()} documents
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadCollectionBackup(coll.name)}
                    disabled={downloadingCollection === coll.name}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors disabled:opacity-50"
                  >
                    {downloadingCollection === coll.name ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Backup
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Backup History */}
        {backupHistory.length > 0 && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-500" />
                Recent Backups (This Browser)
              </h2>
            </div>
            <div className="divide-y">
              {backupHistory.map((backup) => (
                <div key={backup.id} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <div className="font-medium">{backup.type}</div>
                      <div className="text-sm text-gray-500">
                        {backup.collections} collections • {backup.documents?.toLocaleString()} documents
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{backup.filename}</div>
                    <div className="text-xs text-gray-400">{formatDate(backup.date)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-800 mb-2">Backup Best Practices</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• <strong>Before Deployment:</strong> Always download a full backup before redeploying</li>
                <li>• <strong>Regular Backups:</strong> Create weekly backups of critical collections (bookings, leads, users)</li>
                <li>• <strong>Store Safely:</strong> Save backup files to Google Drive, Dropbox, or external storage</li>
                <li>• <strong>Test Restore:</strong> Periodically verify your backups can be restored</li>
                <li>• <strong>Multiple Copies:</strong> Keep at least 3 recent backups in different locations</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Restore Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <HardDrive className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-blue-800 mb-2">How to Restore (If Needed)</h3>
              <div className="text-sm text-blue-700 space-y-2">
                <p>If you need to restore data from a backup:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Contact Emergent support with your backup file</li>
                  <li>Or use MongoDB tools to import the JSON data</li>
                  <li>For urgent issues, use the platform's Rollback feature first</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SalesLayout>
  );
}

export default DatabaseBackup;
